import { expVal } from '@confluence/feature-experiments';

import type { HomeRecentAgentSessionAssociationsQuery$variables } from './__generated__/HomeRecentAgentSessionAssociationsQuery.graphql.ts';
import type { HomeRecentAgentSessionsQuery$variables } from './__generated__/HomeRecentAgentSessionsQuery.graphql.ts';

/**
 * How many of the fetched sessions are actually rendered. Anything the backend returns beyond this is overscan.
 */
export const RECENT_AGENT_SESSIONS_DISPLAY_COUNT = 2;

/**
 * The backend applies `first` before `aqlQuery`, so session queries have to overscan.
 */
export const AGENTS_WAITING_FOR_YOUR_INPUT_PAGE_SIZE = 25;

const HOUR_MS = 60 * 60 * 1000;

const AGENTS_WAITING_FOR_YOUR_INPUT_MAX_AGE_MS = 3 * 24 * HOUR_MS;

/**
 * The section "Agents waiting for your input" shows sessions whose state changed within a 72-hour window by default.
 *
 * The bound is floored to the hour so that the SSR preload and the hydrating client compute the same query
 * variables (and hit the same Relay store records) instead of each computing its own millisecond.
 */
export const getAgentsWaitingForYourInputSince = (now: number = Date.now()): number =>
	Math.floor(
		(now -
			(expVal<number | null>('cc_ai_agentic_sessions', 'maxAgeMs', null) ||
				AGENTS_WAITING_FOR_YOUR_INPUT_MAX_AGE_MS)) /
			HOUR_MS,
	) * HOUR_MS;

const escapeAqlValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

/**
 * Set by the backend on Content Review sessions whose suggestions are ready to review. These sessions are
 * otherwise indistinguishable from other `CONFLUENCE_CHAT_EDIT` sessions in the `ARTEFACT_READY` state.
 */
export const CONTENT_REVIEW_READY_STATE_REASON = 'CONTENT_REVIEW_READY';
export const CONFLUENCE_CREATION_READY_STATE_REASON = 'CONFLUENCE_CREATION_READY';

const CWR_FILTER =
	"((invocationType = 'CONFLUENCE_CWR_EDIT'" +
	" OR invocationType = 'CONFLUENCE_CWR_CREATE'" +
	" OR invocationType = 'CONFLUENCE_CWR_EXISTING')" +
	" AND (state = 'ARTEFACT_READY' OR state = 'INPUT_REQUIRED'))" +
	` OR (stateReason = '${CONFLUENCE_CREATION_READY_STATE_REASON}' AND state = 'ARTEFACT_READY')`;

const CONTENT_REVIEW_FILTER = `(stateReason = '${CONTENT_REVIEW_READY_STATE_REASON}' AND state = 'ARTEFACT_READY')`;

const REMIX_FILTER =
	"((invocationType = 'CONFLUENCE_REMIX' OR invocationType = 'CONFLUENCE_REMIX_EDIT')" +
	" AND state = 'INPUT_REQUIRED')";

const HERO_FILTER = `${CWR_FILTER} OR ${CONTENT_REVIEW_FILTER} OR ${REMIX_FILTER}`;

export const getRecentAgentSessionsQueryVariables = ({
	userId,
	rovoActivationId,
	first = AGENTS_WAITING_FOR_YOUR_INPUT_PAGE_SIZE,
	since,
}: {
	userId: string;
	rovoActivationId: string;
	first?: number;
	/** Only sessions whose `lastStateChangeAt` is at or after this epoch millisecond. */
	since?: number;
}): HomeRecentAgentSessionsQuery$variables => {
	return {
		aqlQuery:
			`authorAccountId = '${escapeAqlValue(userId)}'${getSinceFilter(since)}` +
			` AND (${expVal<string | null>('cc_ai_agentic_sessions', 'aqlFilter', null) ?? HERO_FILTER})`,
		shardingContext: `ari:cloud:rovo::workspace/${rovoActivationId}`,
		first,
	};
};

/**
 * Associations are keyed by (session, entity), so one session can hold several. This bounds the
 * scoped association query generously enough that no requested session is silently truncated away.
 */
const MAX_ASSOCIATIONS_PER_SESSION = 5;

export const getRecentAgentSessionAssociationsQueryVariables = ({
	associatedEntities,
	first = associatedEntities.length * MAX_ASSOCIATIONS_PER_SESSION,
	rovoActivationId,
	since,
	userId,
}: {
	associatedEntities: ReadonlyArray<{ entityId: string; sessionId: string }>;
	/**
	 * The backend applies `first` before `aqlQuery` here too, so the default does not actually bound the
	 * associations of the requested sessions. Paginate with `since` instead.
	 */
	first?: number;
	rovoActivationId: string;
	/** Only associations whose `lastStateChangeAt` (that of their session) is at or after this epoch millisecond. */
	since?: number;
	userId: string;
}): HomeRecentAgentSessionAssociationsQuery$variables => {
	const sessionEntityFilter = associatedEntities
		.map(
			({ entityId, sessionId }) =>
				`(entityId = '${escapeAqlValue(entityId)}' AND agentSessionId = '${escapeAqlValue(
					sessionId,
				)}')`,
		)
		.join(' OR ');
	return {
		aqlQuery:
			`userAccountId = '${escapeAqlValue(userId)}'` +
			` AND deleted = false${getSinceFilter(since)}` +
			` AND (${sessionEntityFilter})`,
		first,
		shardingContext: `ari:cloud:rovo::workspace/${rovoActivationId}`,
	};
};

/**
 * The backend bounds which part of its index it reads only with top-level AND terms, so `lastStateChangeAt`
 * MUST NOT be nested inside parentheses. Everything else (e.g. `state`) is applied to each page after `first`.
 */
const getSinceFilter = (since: number | undefined): string =>
	since === undefined ? '' : ` AND lastStateChangeAt >= ${since}`;
